import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  CheckCircleRounded,
  CancelRounded,
  DeleteRounded,
  RefreshRounded,
  OpenInNewRounded,
} from "@mui/icons-material";
import { useEffect, useState } from "react";
import {
  getAllDocuments,
  reviewDocument,
  deleteAdminDocument,
  getDocumentUrl,
  type AdminBenefitDocument,
} from "../api/tenant-documents";
import { ConfirmDialog } from "./ConfirmDialog";
import { useNotifications } from "../components/NotificationProvider";

type DocumentReviewListProps = {
  token: string;
};

export function DocumentReviewList({ token }: DocumentReviewListProps) {
  const [documents, setDocuments] = useState<AdminBenefitDocument[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState<string>("");
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<AdminBenefitDocument | null>(
    null,
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const { showAlert, showSnackbar } = useNotifications();

  const loadDocuments = async (status?: string) => {
    setIsLoading(true);
    try {
      const data = await getAllDocuments(token, status);
      setDocuments(data);
    } catch (error) {
      console.error(error);
      showAlert({
        title: "Unable to load documents",
        message: "Please try again shortly.",
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadDocuments(filter || undefined);
  }, [token, filter]);

  const handleApprove = async (doc: AdminBenefitDocument) => {
    try {
      await reviewDocument(token, doc.id, "approved");
      showSnackbar({
        title: "Document approved",
        message: `${doc.file_name} has been approved.`,
        variant: "success",
      });
      void loadDocuments(filter || undefined);
    } catch (error) {
      showAlert({
        title: "Approval failed",
        message: "Could not approve the document.",
        variant: "error",
      });
    }
  };

  const openRejectDialog = (doc: AdminBenefitDocument) => {
    setSelectedDoc(doc);
    setRejectionReason("");
    setRejectDialogOpen(true);
  };

  const handleReject = async () => {
    if (!selectedDoc) return;

    try {
      await reviewDocument(token, selectedDoc.id, "rejected", rejectionReason);
      showSnackbar({
        title: "Document rejected",
        message: `${selectedDoc.file_name} has been rejected.`,
        variant: "info",
      });
      setRejectDialogOpen(false);
      setSelectedDoc(null);
      void loadDocuments(filter || undefined);
    } catch (error) {
      showAlert({
        title: "Rejection failed",
        message: "Could not reject the document.",
        variant: "error",
      });
    }
  };

  const openDeleteDialog = (doc: AdminBenefitDocument) => {
    setSelectedDoc(doc);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedDoc) return;

    setDeleteLoading(true);
    try {
      await deleteAdminDocument(token, selectedDoc.id);
      showSnackbar({
        title: "Document deleted",
        message: `${selectedDoc.file_name} has been deleted.`,
        variant: "success",
      });
      setDeleteDialogOpen(false);
      setSelectedDoc(null);
      void loadDocuments(filter || undefined);
    } catch (error) {
      showAlert({
        title: "Delete failed",
        message: "Could not delete the document.",
        variant: "error",
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "approved":
        return "success";
      case "rejected":
        return "error";
      default:
        return "warning";
    }
  };

  const pendingCount = documents.filter((d) => d.status === "pending").length;

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
        <Chip
          label={`All (${documents.length})`}
          onClick={() => setFilter("")}
          variant={filter === "" ? "filled" : "outlined"}
          color="primary"
          sx={{ borderRadius: 6, fontWeight: 600 }}
        />
        <Chip
          label={`Pending (${pendingCount})`}
          onClick={() => setFilter("pending")}
          variant={filter === "pending" ? "filled" : "outlined"}
          color="warning"
          sx={{ borderRadius: 6, fontWeight: 600 }}
        />
        <Chip
          label="Approved"
          onClick={() => setFilter("approved")}
          variant={filter === "approved" ? "filled" : "outlined"}
          color="success"
          sx={{ borderRadius: 6, fontWeight: 600 }}
        />
        <Chip
          label="Rejected"
          onClick={() => setFilter("rejected")}
          variant={filter === "rejected" ? "filled" : "outlined"}
          color="error"
          sx={{ borderRadius: 6, fontWeight: 600 }}
        />
        <Tooltip title="Refresh">
          <IconButton
            onClick={() => void loadDocuments(filter || undefined)}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 6,
            }}
          >
            <RefreshRounded fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      {isLoading && <LinearProgress sx={{ borderRadius: 6 }} />}

      <Stack spacing={2}>
        {documents.map((doc) => (
          <Card
            key={doc.id}
            variant="outlined"
            sx={{
              borderRadius: 6,
              transition: "box-shadow 0.2s ease, transform 0.2s ease",
              "&:hover": {
                boxShadow: 2,
                borderColor: "primary.main",
              },
            }}
          >
            <CardContent
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 2,
                flexWrap: "wrap",
                "&:last-child": { pb: 2 },
              }}
            >
              <Box sx={{ flex: 1, minWidth: 260 }}>
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <Avatar
                    sx={{
                      bgcolor: "primary.main",
                      width: 42,
                      height: 42,
                      fontWeight: 700,
                    }}
                  >
                    {doc.tenant_name?.charAt(0) ?? "T"}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {doc.tenant_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {doc.tenant_email}
                    </Typography>
                  </Box>
                </Stack>

                <Stack spacing={0.5} sx={{ mt: 1.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {doc.file_name}
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Typography variant="caption" color="text.secondary">
                      {formatFileSize(doc.file_size)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      • {new Date(doc.uploaded_at).toLocaleDateString()}
                    </Typography>
                  </Stack>
                  {doc.rejection_reason && (
                    <Typography
                      variant="caption"
                      color="error.main"
                      sx={{ mt: 0.5 }}
                    >
                      Rejection reason: {doc.rejection_reason}
                    </Typography>
                  )}
                  {doc.reviewed_by_name && (
                    <Typography variant="caption" color="text.secondary">
                      Reviewed by: {doc.reviewed_by_name}
                    </Typography>
                  )}
                </Stack>
              </Box>

              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  label={doc.status}
                  size="small"
                  color={getStatusColor(doc.status) as any}
                  sx={{
                    borderRadius: 6,
                    fontWeight: 600,
                    textTransform: "capitalize",
                  }}
                />
                <Tooltip title="View file">
                  <IconButton
                    size="small"
                    onClick={async () => {
                      try {
                        const response = await fetch(getDocumentUrl(doc.id), {
                          headers: { Authorization: `Bearer ${token}` },
                        });
                        if (!response.ok) throw new Error("Failed to load");
                        const blob = await response.blob();
                        const url = URL.createObjectURL(blob);
                        window.open(url, "_blank", "noopener,noreferrer");
                      } catch {
                        showAlert({
                          title: "Cannot open file",
                          message: "Failed to load the document.",
                          variant: "error",
                        });
                      }
                    }}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 6,
                    }}
                  >
                    <OpenInNewRounded fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete document">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => openDeleteDialog(doc)}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 6,
                    }}
                  >
                    <DeleteRounded fontSize="small" />
                  </IconButton>
                </Tooltip>
                {doc.status === "pending" && (
                  <>
                    <Tooltip title="Approve">
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => handleApprove(doc)}
                        sx={{
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 6,
                        }}
                      >
                        <CheckCircleRounded fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Reject">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => openRejectDialog(doc)}
                        sx={{
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 6,
                        }}
                      >
                        <CancelRounded fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>

      {documents.length === 0 && !isLoading && (
        <Paper
          variant="outlined"
          sx={{ borderRadius: 6, p: 4, textAlign: "center" }}
        >
          <Typography variant="body1" color="text.secondary">
            No documents found for the selected filter.
          </Typography>
        </Paper>
      )}

      <Dialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 6, maxWidth: 480 } }}
      >
        <DialogTitle>Reject Document</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Are you sure you want to reject{" "}
            <strong>{selectedDoc?.file_name}</strong>?
          </Typography>
          <TextField
            label="Rejection reason (optional)"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            multiline
            minRows={3}
            fullWidth
            placeholder="Provide a reason for rejection..."
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 6 } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setRejectDialogOpen(false)}
            color="inherit"
            sx={{ borderRadius: 6 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleReject}
            color="error"
            variant="contained"
            disableElevation
            sx={{ borderRadius: 6 }}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete document?"
        message={`This will permanently delete ${selectedDoc?.file_name ?? "this document"} and its uploaded file.`}
        confirmLabel={deleteLoading ? "Deleting..." : "Delete"}
        onConfirm={() => void handleDelete()}
        onClose={() => {
          if (!deleteLoading) {
            setDeleteDialogOpen(false);
            setSelectedDoc(null);
          }
        }}
      />
    </Stack>
  );
}
